import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Checkbox } from "@/components/ui/checkbox";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";
import { Building2, Users, Heart, Info, ArrowLeft, ArrowRight } from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { jobPostingSchema } from "@/lib/validations/job-posting";

export default function PostJob() {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [step, setStep] = useState(1);
  const [skills, setSkills] = useState<string[]>([]);
  const [currentSkill, setCurrentSkill] = useState("");
  
  const [formData, setFormData] = useState({
    jobTitle: "",
    companyName: "",
    location: "",
    employmentType: "",
    salaryMin: "",
    salaryMax: "",
    salaryPeriod: "per year",
    description: "",
    experienceLevel: "",
    openToCandidates: true,
  });

  const handleSkillAdd = () => {
    if (currentSkill.trim() && !skills.includes(currentSkill.trim())) {
      if (skills.length >= 20) {
        toast.error("You can add up to 20 skills");
        return;
      }
      if (currentSkill.trim().length > 50) {
        toast.error("Each skill must be less than 50 characters");
        return;
      }
      setSkills([...skills, currentSkill.trim()]);
      setCurrentSkill("");
    }
  };

  const handleSkillRemove = (skill: string) => {
    setSkills(skills.filter(s => s !== skill));
  };

  const handleSubmit = async () => {
    setLoading(true);

    try {
      const { data: { user } } = await supabase.auth.getUser();
      
      if (!user) {
        toast.error("You must be logged in to post a job");
        return;
      }

      // Validate form data with Zod schema
      const validationResult = jobPostingSchema.safeParse({
        jobTitle: formData.jobTitle,
        companyName: formData.companyName,
        location: formData.location,
        employmentType: formData.employmentType,
        salaryMin: formData.salaryMin,
        salaryMax: formData.salaryMax,
        description: formData.description,
        skills: skills,
        openToCandidates: formData.openToCandidates,
      });

      if (!validationResult.success) {
        const firstError = validationResult.error.issues[0];
        toast.error(firstError.message);
        return;
      }

      const { error } = await supabase.from("opportunities").insert({
        employer_id: user.id,
        title: validationResult.data.jobTitle,
        company_name: validationResult.data.companyName,
        location: validationResult.data.location,
        employment_type: validationResult.data.employmentType,
        salary_min: parseInt(validationResult.data.salaryMin),
        salary_max: parseInt(validationResult.data.salaryMax),
        description: validationResult.data.description,
        requirements: validationResult.data.skills.join(", "),
        open_to_candidates_with_records: validationResult.data.openToCandidates,
        disclosure_level_required: validationResult.data.openToCandidates ? 0 : 1,
        status: "active",
        expires_at: new Date(Date.now() + 30 * 24 * 60 * 60 * 1000).toISOString(), // 30 days
      });

      if (error) throw error;

      toast.success("Your job listing is now live!");
      navigate("/employer/dashboard");
    } catch (error: any) {
      toast.error(error.message || "Failed to post job");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-white">
      {/* Header */}
      <header className="border-b">
        <div className="container py-6">
          <Button variant="ghost" onClick={() => navigate("/employer/dashboard")}>
            <ArrowLeft className="mr-2 h-4 w-4" />
            Back to Dashboard
          </Button>
        </div>
      </header>

      {/* Hero Section */}
      <section className="bg-gradient-to-br from-[hsl(var(--employer-navy))] to-[hsl(var(--employer-navy))]/80 text-white py-16">
        <div className="container text-center">
          <h1 className="text-4xl md:text-5xl font-bold mb-4">
            Unlock Untapped Potential. Post Your Job Today.
          </h1>
          <p className="text-lg text-white/90 mb-8 max-w-3xl mx-auto">
            Join London businesses in discovering a motivated, loyal, and overlooked talent pool. 
            Posting is free and takes less than 5 minutes.
          </p>
          <Button 
            size="lg" 
            className="bg-[hsl(var(--employer-teal))] hover:bg-[hsl(var(--employer-teal))]/90"
            onClick={() => document.getElementById('job-form')?.scrollIntoView({ behavior: 'smooth' })}
          >
            Get Started
          </Button>
        </div>
      </section>

      {/* Why Hire Section */}
      <section className="py-16 bg-gray-50">
        <div className="container">
          <h2 className="text-3xl font-bold text-center mb-12">Why Hire Second Chance Talent?</h2>
          
          <div className="grid md:grid-cols-3 gap-8 mb-12">
            <Card>
              <CardHeader>
                <div className="w-12 h-12 rounded-full bg-[hsl(var(--employer-teal))]/10 flex items-center justify-center mb-4">
                  <Users className="w-6 h-6 text-[hsl(var(--employer-teal))]" />
                </div>
                <CardTitle>Higher Retention Rates</CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-muted-foreground">
                  Second chance hires often show greater loyalty and lower turnover.
                </p>
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <div className="w-12 h-12 rounded-full bg-[hsl(var(--employer-teal))]/10 flex items-center justify-center mb-4">
                  <Heart className="w-6 h-6 text-[hsl(var(--employer-teal))]" />
                </div>
                <CardTitle>Motivated & Engaged</CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-muted-foreground">
                  Access a pool of candidates determined to succeed and prove their potential.
                </p>
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <div className="w-12 h-12 rounded-full bg-[hsl(var(--employer-teal))]/10 flex items-center justify-center mb-4">
                  <Building2 className="w-6 h-6 text-[hsl(var(--employer-teal))]" />
                </div>
                <CardTitle>Strengthen Your Community</CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-muted-foreground">
                  Make a tangible social impact and build a diverse, inclusive workforce.
                </p>
              </CardContent>
            </Card>
          </div>

          {/* Testimonial */}
          <Card className="bg-[hsl(var(--employer-navy))] text-white border-0">
            <CardContent className="p-8">
              <p className="text-lg italic mb-4">
                "Hiring through Second Chance Connect was the best decision we made this year. 
                We found a dedicated team member and the support from the platform was fantastic."
              </p>
              <p className="font-semibold">- Maria, Owner of a London Café</p>
            </CardContent>
          </Card>
        </div>
      </section>

      {/* Job Posting Form */}
      <section id="job-form" className="py-16">
        <div className="container max-w-3xl">
          <div className="mb-8">
            <h2 className="text-3xl font-bold mb-2">Post Your Job</h2>
            <p className="text-muted-foreground">Complete these simple steps to reach great candidates</p>
          </div>

          {/* Progress Indicator */}
          <div className="flex items-center justify-center mb-8">
            {[1, 2, 3].map((num) => (
              <div key={num} className="flex items-center">
                <div
                  className={`w-10 h-10 rounded-full flex items-center justify-center font-semibold ${
                    step >= num
                      ? "bg-[hsl(var(--employer-teal))] text-white"
                      : "bg-gray-200 text-gray-600"
                  }`}
                >
                  {num}
                </div>
                {num < 3 && (
                  <div
                    className={`w-16 h-1 ${
                      step > num ? "bg-[hsl(var(--employer-teal))]" : "bg-gray-200"
                    }`}
                  />
                )}
              </div>
            ))}
          </div>

          {/* Step 1: The Basics */}
          {step === 1 && (
            <Card>
              <CardHeader>
                <CardTitle>The Basics</CardTitle>
                <CardDescription>Tell us about the position</CardDescription>
              </CardHeader>
              <CardContent className="space-y-5">
                <div className="space-y-2">
                  <Label htmlFor="jobTitle">Job Title *</Label>
                  <Input
                    id="jobTitle"
                    value={formData.jobTitle}
                    onChange={(e) => setFormData({ ...formData, jobTitle: e.target.value })}
                    placeholder="e.g., Customer Service Representative"
                    required
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="companyName">Company Name *</Label>
                  <Input
                    id="companyName"
                    value={formData.companyName}
                    onChange={(e) => setFormData({ ...formData, companyName: e.target.value })}
                    placeholder="Your Company Ltd"
                    required
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="location">Workplace Location (Postcode in London) *</Label>
                  <Input
                    id="location"
                    value={formData.location}
                    onChange={(e) => setFormData({ ...formData, location: e.target.value })}
                    placeholder="E1 6AN"
                    required
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="employmentType">Type of Employment *</Label>
                  <Select value={formData.employmentType} onValueChange={(value) => setFormData({ ...formData, employmentType: value })}>
                    <SelectTrigger>
                      <SelectValue placeholder="Select employment type" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="Full-time">Full-time</SelectItem>
                      <SelectItem value="Part-time">Part-time</SelectItem>
                      <SelectItem value="Contract">Contract</SelectItem>
                      <SelectItem value="Apprenticeship">Apprenticeship</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                <div className="grid grid-cols-3 gap-4">
                  <div className="col-span-1 space-y-2">
                    <Label htmlFor="salaryMin">Min Salary *</Label>
                    <Input
                      id="salaryMin"
                      type="number"
                      value={formData.salaryMin}
                      onChange={(e) => setFormData({ ...formData, salaryMin: e.target.value })}
                      placeholder="25000"
                      required
                    />
                  </div>
                  <div className="col-span-1 space-y-2">
                    <Label htmlFor="salaryMax">Max Salary *</Label>
                    <Input
                      id="salaryMax"
                      type="number"
                      value={formData.salaryMax}
                      onChange={(e) => setFormData({ ...formData, salaryMax: e.target.value })}
                      placeholder="30000"
                      required
                    />
                  </div>
                  <div className="col-span-1 space-y-2">
                    <Label htmlFor="salaryPeriod">Period</Label>
                    <Select value={formData.salaryPeriod} onValueChange={(value) => setFormData({ ...formData, salaryPeriod: value })}>
                      <SelectTrigger>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="per hour">per hour</SelectItem>
                        <SelectItem value="per year">per year</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>

                <div className="flex justify-end">
                  <Button onClick={() => setStep(2)}>
                    Next: The Role
                    <ArrowRight className="ml-2 h-4 w-4" />
                  </Button>
                </div>
              </CardContent>
            </Card>
          )}

          {/* Step 2: The Role */}
          {step === 2 && (
            <Card>
              <CardHeader>
                <CardTitle>The Role</CardTitle>
                <CardDescription>Describe what the job entails</CardDescription>
              </CardHeader>
              <CardContent className="space-y-5">
                <div className="space-y-2">
                  <div className="flex items-center gap-2 mb-2">
                    <Label htmlFor="description">Job Description *</Label>
                    <div className="group relative">
                      <Info className="h-4 w-4 text-muted-foreground cursor-help" />
                      <div className="absolute left-0 top-6 w-80 p-4 bg-white border rounded-lg shadow-lg hidden group-hover:block z-10">
                        <p className="text-sm font-semibold mb-2">💡 Tip: Write an inclusive job description</p>
                        <ul className="text-sm space-y-1 text-muted-foreground">
                          <li>• Focus on what the person will do, not what they must have</li>
                          <li>• Use welcoming language like "We encourage people from all backgrounds to apply"</li>
                          <li>• Avoid corporate jargon and overly formal language</li>
                          <li>• Be clear about expectations and growth opportunities</li>
                        </ul>
                      </div>
                    </div>
                  </div>
                  <Textarea
                    id="description"
                    value={formData.description}
                    onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                    placeholder="Describe the day-to-day responsibilities, what success looks like in this role, and why someone would enjoy working here..."
                    rows={8}
                    required
                  />
                </div>

                <div className="flex gap-4">
                  <Button variant="outline" onClick={() => setStep(1)}>
                    <ArrowLeft className="mr-2 h-4 w-4" />
                    Back
                  </Button>
                  <Button onClick={() => setStep(3)}>
                    Next: The Ideal Candidate
                    <ArrowRight className="ml-2 h-4 w-4" />
                  </Button>
                </div>
              </CardContent>
            </Card>
          )}

          {/* Step 3: The Ideal Candidate */}
          {step === 3 && (
            <Card>
              <CardHeader>
                <CardTitle>The Ideal Candidate</CardTitle>
                <CardDescription>What skills and experience are you looking for?</CardDescription>
              </CardHeader>
              <CardContent className="space-y-5">
                <div className="space-y-2">
                  <Label htmlFor="skills">Key Skills & Qualifications</Label>
                  <div className="flex gap-2">
                    <Input
                      id="skills"
                      value={currentSkill}
                      onChange={(e) => setCurrentSkill(e.target.value)}
                      onKeyPress={(e) => e.key === "Enter" && (e.preventDefault(), handleSkillAdd())}
                      placeholder="Type a skill and press Enter"
                    />
                    <Button type="button" onClick={handleSkillAdd}>Add</Button>
                  </div>
                  <div className="flex flex-wrap gap-2 mt-2">
                    {skills.map((skill) => (
                      <Badge key={skill} variant="secondary" className="cursor-pointer" onClick={() => handleSkillRemove(skill)}>
                        {skill} ×
                      </Badge>
                    ))}
                  </div>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="experienceLevel">Experience Level</Label>
                  <Select value={formData.experienceLevel} onValueChange={(value) => setFormData({ ...formData, experienceLevel: value })}>
                    <SelectTrigger>
                      <SelectValue placeholder="Select experience level" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="Entry-level">Entry-level</SelectItem>
                      <SelectItem value="Intermediate">Intermediate</SelectItem>
                      <SelectItem value="Experienced">Experienced</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                <div className="flex items-start space-x-2 p-4 bg-green-50 rounded-lg border border-green-200">
                  <Checkbox
                    id="openToCandidates"
                    checked={formData.openToCandidates}
                    onCheckedChange={(checked) => setFormData({ ...formData, openToCandidates: checked as boolean })}
                  />
                  <div className="space-y-1">
                    <Label htmlFor="openToCandidates" className="cursor-pointer font-semibold text-green-900">
                      ✓ This role is open to candidates with a criminal record
                    </Label>
                    <p className="text-sm text-green-700">
                      Recommended: This helps build an inclusive talent pool and gives candidates a second chance.
                    </p>
                  </div>
                </div>

                <div className="flex gap-4">
                  <Button variant="outline" onClick={() => setStep(2)}>
                    <ArrowLeft className="mr-2 h-4 w-4" />
                    Back
                  </Button>
                  <Button onClick={() => setStep(4)}>
                    Review & Post
                    <ArrowRight className="ml-2 h-4 w-4" />
                  </Button>
                </div>
              </CardContent>
            </Card>
          )}

          {/* Step 4: Review & Post */}
          {step === 4 && (
            <Card>
              <CardHeader>
                <CardTitle>Review Your Job Listing</CardTitle>
                <CardDescription>Make sure everything looks correct before posting</CardDescription>
              </CardHeader>
              <CardContent className="space-y-6">
                <div className="space-y-4">
                  <div>
                    <h3 className="font-semibold text-lg">{formData.jobTitle}</h3>
                    <p className="text-muted-foreground">{formData.companyName} • {formData.location}</p>
                    <p className="text-sm text-muted-foreground mt-1">
                      {formData.employmentType} • £{formData.salaryMin} - £{formData.salaryMax} {formData.salaryPeriod}
                    </p>
                  </div>

                  <div>
                    <h4 className="font-semibold mb-2">Job Description</h4>
                    <p className="text-muted-foreground whitespace-pre-wrap">{formData.description}</p>
                  </div>

                  {skills.length > 0 && (
                    <div>
                      <h4 className="font-semibold mb-2">Required Skills</h4>
                      <div className="flex flex-wrap gap-2">
                        {skills.map((skill) => (
                          <Badge key={skill} variant="secondary">{skill}</Badge>
                        ))}
                      </div>
                    </div>
                  )}

                  {formData.experienceLevel && (
                    <div>
                      <h4 className="font-semibold mb-2">Experience Level</h4>
                      <p className="text-muted-foreground">{formData.experienceLevel}</p>
                    </div>
                  )}

                  {formData.openToCandidates && (
                    <div className="flex items-center gap-2 text-green-700">
                      <Heart className="h-4 w-4" />
                      <span className="text-sm font-medium">Open to candidates with a criminal record</span>
                    </div>
                  )}
                </div>

                <div className="flex gap-4">
                  <Button variant="outline" onClick={() => setStep(3)}>
                    <ArrowLeft className="mr-2 h-4 w-4" />
                    Edit
                  </Button>
                  <Button 
                    onClick={handleSubmit} 
                    disabled={loading}
                    className="bg-[hsl(var(--employer-teal))] hover:bg-[hsl(var(--employer-teal))]/90"
                  >
                    {loading ? "Posting..." : "Post Job Listing"}
                  </Button>
                </div>

                <div className="p-4 bg-blue-50 rounded-lg border border-blue-200">
                  <p className="text-sm text-blue-900">
                    📧 Your job is now visible to our network of candidates. You will be notified by email when you receive an application.
                  </p>
                </div>
              </CardContent>
            </Card>
          )}
        </div>
      </section>
    </div>
  );
}
